import { Chip, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useGetOrganizationSettings from "hooks/organization/useGetOrganizationSettings";
import { CustomJobPhase } from "hooks/jobs/job-phases/useGetJobPhase";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import TourHelpButton from "components/PageTour/TourHelpButton";
import {
  DEFAULT_JOB_COSTING_THRESHOLD_PERCENT,
  deriveJobCostingVisibilityMetrics,
  getJobCostingStatusMeta,
} from "utils/jobs/jobCostingVisibility";

export default function JobCostingVisibilitySummary({
  jobPhase,
  onHelpClick,
}: {
  jobPhase: CustomJobPhase;
  onHelpClick?: () => void;
}) {
  const { data: organizationSettings } = useGetOrganizationSettings();
  const thresholdPercent = Number(
    organizationSettings?.job_costing_variance_threshold_percent
      ? JSON.parse(organizationSettings.job_costing_variance_threshold_percent)
      : DEFAULT_JOB_COSTING_THRESHOLD_PERCENT
  );

  const metrics = deriveJobCostingVisibilityMetrics({
    actualCostAdditional: jobPhase.actualCostAdditional,
    actualCostEquipment: jobPhase.actualCostEquipment,
    actualCostLabour: jobPhase.actualCostLabour,
    actualCostMaterial: jobPhase.actualCostMaterial,
    actualCostTotal: jobPhase.actualCostTotal,
    actualCostTransferTruck: jobPhase.actualCostTransferTruck,
    proposedCostAdditional: jobPhase.proposedCostAdditional,
    proposedCostEquipment: jobPhase.proposedCostEquipment,
    proposedCostLabour: jobPhase.proposedCostLabour,
    proposedCostMaterial: jobPhase.proposedCostMaterial,
    proposedCostTotal: jobPhase.proposedCostTotal,
    proposedCostTransferTruck: jobPhase.proposedCostTransferTruck,
    costItems: jobPhase.costItems,
    thresholdPercent,
  });
  const statusMeta = getJobCostingStatusMeta(metrics.jobCostingCompletionStatus);

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="flex-start" alignItems="center" gap={1} mb={2.5}>
        <MDTypography variant="h6">Job Costing Visibility</MDTypography>
        {onHelpClick && (
          <TourHelpButton
            tooltip="Click here to learn more about Job Costing"
            onClick={onHelpClick}
          />
        )}
      </MDBox>
      <MDBox mb={2.5}>
        <Chip
          label={statusMeta.label}
          size="small"
          sx={{
            color: statusMeta.chipColor,
            backgroundColor: statusMeta.chipBackground,
            fontWeight: 700,
          }}
        />
      </MDBox>
      <MDBox
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(5, minmax(0, 1fr))",
          },
          gap: 2.5,
          width: "100%",
        }}
      >
        <MDBox>
          <MDTypography variant="button" color="text">
            Costed?
          </MDTypography>
          <MDTypography variant="h6" color={metrics.hasJobCostsEntered ? "success" : "error"}>
            {metrics.hasJobCostsEntered ? "Yes" : "No"}
          </MDTypography>
        </MDBox>
        <MDBox>
          <Tooltip title={`Threshold: ${metrics.jobCostingThresholdPercent}%`} arrow placement="top">
            <MDTypography
              variant="button"
              color="text"
              sx={{ display: "inline-flex", textDecoration: "underline dotted", cursor: "help" }}
            >
              Variance
            </MDTypography>
          </Tooltip>
          <MDTypography
            variant="h6"
            color={
              metrics.jobCostingCompletionStatus === "WITHIN_THRESHOLD"
                ? "success"
                : metrics.jobCostingCompletionStatus === "IN_PROGRESS"
                  ? "warning"
                  : "error"
            }
          >
            {metrics.jobCostingVariancePercent.toFixed(2)}%
          </MDTypography>
        </MDBox>
        <MDBox>
          <MDTypography variant="button" color="text">
            Proposed Total
          </MDTypography>
          <MDTypography variant="h6">
            {formatCentsToUSD(Number(jobPhase.proposedCostTotal ?? 0))}
          </MDTypography>
        </MDBox>
        <MDBox>
          <MDTypography variant="button" color="text">
            Actual Total
          </MDTypography>
          <MDTypography variant="h6">
            {formatCentsToUSD(Number(jobPhase.actualCostTotal ?? 0))}
          </MDTypography>
        </MDBox>
        <MDBox>
          <MDTypography variant="button" color="text">
            Costing Status
          </MDTypography>
          <MDTypography variant="h6">{statusMeta.label}</MDTypography>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
